import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { inCommands } from '../../../concepts/commands/CommandRegistry';
import { stringStep } from '../../../concepts/commands/CommandWizard';
import { ObjectManager } from '../../../concepts/objects';
import { EditorSession } from '../../canvas/EditorSession';
import { Selection } from '../../canvas/Selection';
import { resolvePhysical } from '../../canvas/commands/CommandUtil';
import { RelativeTo } from '../../canvas/entities/Physical';
import { EffectDirector, Effects } from '../../canvas/visualization/EffectDirector';
import { AutoLayout } from '../autoLayout/AutoLayout';
import { autoLayout } from '../autoLayout/AutoLayoutCommands';
import { SmartContainer } from './SmartContainer';

export const pipeNext = defineCommand(
    'pipeNext',
    'Pipe: Next',
    (cc, [fromContainer, toContainer, order]: [any, any, string?]) => {

        const objects = cc.resolve(ObjectManager);

        const from = resolvePhysical(cc, fromContainer, SmartContainer);
        const to = resolvePhysical(cc, toContainer, SmartContainer);
        if (!from || !to) return;

        const all = from.getPhysicalsNaturallyOrdered();
        if (!all.length) return;
        if (order === 'reverse' || order === 'inverse') {
            all.reverse();
        }

        const [next] = all;
        if (!next) return;

        const command = to.queryPipe(next);
        if (!command) return;

        objects.update(() => {

            cc.dispatch(command, [to.id, next.id]);

            if (from.is(AutoLayout)) {
                cc.dispatch(autoLayout, [from.id]);
            }

        });

    },
    [
        stringStep('fromContainer', 'From Container'),
        stringStep('toContainer', 'To Container'),
        stringStep('order', 'Order [default|inverse]'),
    ],
);

export const pipeAll = defineCommand(
    'pipeAll',
    'Pipe: All',
    (cc, [fromContainer, toContainer, order]: [string, string, string?]) => {

        const objects = cc.resolve(ObjectManager);
        const effects = cc.resolve(EffectDirector);

        const from = resolvePhysical(cc, fromContainer, SmartContainer);
        const to = resolvePhysical(cc, toContainer, SmartContainer);
        if (!from || !to) return;

        const all = from.getPhysicalsNaturallyOrdered();
        if (!all.length) return;
        if (order === 'reverse' || order === 'inverse') {
            all.reverse();
        }

        objects.update(() => {

            for (const next of all) {
                const command = to.queryPipe(next);
                if (!command) continue;
                const fmt = next.transform(RelativeTo.Model);
                cc.dispatch(command, [to.id, next.id]);
                const tmt = next.transform(RelativeTo.Model);
                effects.trigger(Effects.tween(fmt, tmt), next);
            }

            if (from.is(AutoLayout)) {
                cc.dispatch(autoLayout, [from.id]);
            }

        });
    },
    [
        stringStep('fromContainer', 'From Container'),
        stringStep('toContainer', 'To Container'),
        stringStep('order', 'Order [default|inverse]'),
    ],
);

export const pipeSelection = defineCommand(
    'pipeSelection',
    'Pipe: Selection',
    (cc, [toContainer, order]: [string, string, string?]) => {

        const objects = cc.resolve(ObjectManager);
        const effects = cc.resolve(EffectDirector);
        const session = cc.resolve(EditorSession);

        const to = resolvePhysical(cc, toContainer, SmartContainer);
        if (!to) return;

        const all = session.get(Selection).toArray();
        if (!all.length) return;
        if (order === 'reverse' || order === 'inverse') {
            all.reverse();
        }

        objects.update(() => {

            for (const next of all) {
                const command = to.queryPipe(next);
                if (!command) continue;
                const fmt = next.transform(RelativeTo.Model);
                cc.dispatch(command, [to.id, next.id]);
                const tmt = next.transform(RelativeTo.Model);
                effects.trigger(Effects.tween(fmt, tmt), next);
            }

        });
    },
    [
        stringStep('toContainer', 'To Container'),
        stringStep('order', 'Order [default|inverse]'),
    ],
);

export const inSmartContainerCommands = () => inCommands(
    pipeNext,
    pipeAll,
    pipeSelection,
);
